// LEGACY: Contains significant business logic in component. Consider extracting logic into custom hooks, utility functions, or /shared/ directory.
/* eslint-disable filename-export/match-default-export */

import { isEmpty, isNil, isObject as _isObject, toString as _toString } from 'lodash';
import CONSENT_SOURCE_TYPES, {
  DEFAULT_CONSENT_SOURCE_TYPE,
  DEFAULT_COOKIE_ENCODING,
  DEFAULT_OPT_IN_VALUES,
  isNonCookieConsentSource,
} from './CONSENT_SOURCE_TYPES';
import { formatOptInValues } from './consentOptInValues';
import deriveCmpPreset from './deriveCmpPreset';
import {
  CONSENT_MATCH_ID_CONSENT_CODE,
  CONSENT_MATCH_ID_ENABLED,
  CONSENT_SOURCE_TYPE,
  CONSENT_SAVED_CALLBACK_SOURCE,
  CONSENT_COOKIE_ENCODING,
  CONSENT_COOKIE_WINDOW_VAR,
  CONSENT_CMP_PRESET,
  CONSENT_ONETRUST_COOKIE_NAME,
  CONSENT_JS_API_GLOBAL_OBJECT,
  CONSENT_JS_API_METHOD,
  CONSENT_JS_API_PROPERTY_PATH,
  CONSENT_OPT_IN_VALUES,
  CONSENT_GPC_ENABLED,
  CONSENT_GPC_CODES,
} from './form-keys';
import EGSM, { EGSM_ENABLED_FIELD, EGSM_SIGNALS } from './EGSM';
import UPDATE_MODES, { DEFAULT_UPDATE_MODE } from './export-import/UPDATE_MODES';

/**
 *
 * Populate analytics builder form with saved analytics file data
 *
 * @params {object} params - Parameters object
 * @params {object} params.analyticsFileData - Analytics file data to populate
 * @params {function} params.setField - Form setField function
 * @params {string} params.updateMode - Update mode (override or merge)
 */

const populateAjsBuilderForm = ({ analyticsFileData, setField, updateMode = DEFAULT_UPDATE_MODE }) => {
  const setFieldIfDefined = (name, value) => {
    if (updateMode === UPDATE_MODES.MERGE && isNil(value)) {
      return;
    }
    setField({
      target: {
        name,
        value,
      },
    });
  };

  // for first time AJS build on a pipeline
  // copy any saved general settings to formData and short-circuit
  // no longer setting a default cookieName value as users kept the default (one trust) and never audited or changed it to match their actual cookie
  if (isEmpty(analyticsFileData)) {
    return;
  }

  if (updateMode === UPDATE_MODES.OVERRIDE) {
    // useForm retains existing values for nullish updates, so replacement imports
    // need concrete defaults before applying the fields the imported file supplies.
    const consentDefaults = {
      cookieName: '',
      [CONSENT_SOURCE_TYPE]: DEFAULT_CONSENT_SOURCE_TYPE,
      [CONSENT_SAVED_CALLBACK_SOURCE]: '',
      [CONSENT_COOKIE_ENCODING]: DEFAULT_COOKIE_ENCODING,
      [CONSENT_COOKIE_WINDOW_VAR]: '',
      [CONSENT_CMP_PRESET]: '',
      [CONSENT_ONETRUST_COOKIE_NAME]: '',
      [CONSENT_JS_API_GLOBAL_OBJECT]: '',
      [CONSENT_JS_API_METHOD]: '',
      [CONSENT_JS_API_PROPERTY_PATH]: '',
      [CONSENT_OPT_IN_VALUES]: DEFAULT_OPT_IN_VALUES,
      [CONSENT_GPC_ENABLED]: 'false',
      [CONSENT_GPC_CODES]: '[]',
      [CONSENT_MATCH_ID_ENABLED]: 'false',
      [CONSENT_MATCH_ID_CONSENT_CODE]: '',
    };
    Object.entries(consentDefaults).forEach(([name, value]) => setFieldIfDefined(name, value));
  }

  setFieldIfDefined('fileCompression', analyticsFileData.settings.compressFile);

  if (!isNil(analyticsFileData.settings.minifyFile)) {
    setFieldIfDefined('unminifyFile', analyticsFileData.settings.minifyFile === false);
  }

  setFieldIfDefined('botFilterEnabled', analyticsFileData.settings.botFilterEnabled);
  setFieldIfDefined(CONSENT_MATCH_ID_CONSENT_CODE, analyticsFileData.settings.matchIDConsentCode);
  // Explicitly enable the anonymousId Consent radio for stored selections: the
  // mount-time DOM scrape can capture 'false' into formState before this
  // populate runs, which would silently display Disabled and clear the code on
  // the next build.
  //
  // Emptiness, not nil: the DB coerces an unset code to NULL, but the YAML
  // export path writes matchIDConsentCodeID: '' verbatim, so an isNil guard
  // turned the radio on for every imported file that had the feature off —
  // and the category select is required, so the build was then blocked.
  if (!isEmpty(analyticsFileData.settings.matchIDConsentCode)) {
    setFieldIfDefined(CONSENT_MATCH_ID_ENABLED, 'true');
  }

  if (!isNil(analyticsFileData.settings.cdnURL)) {
    const cdnURL = analyticsFileData.settings.cdnURL.replace('https://', '');
    setFieldIfDefined('cdnURL', cdnURL);
  }

  setFieldIfDefined('fileHostingType', analyticsFileData.settings.fileHostingType);

  if (!isNil(analyticsFileData.settings.host)) {
    const host = analyticsFileData.settings.host.replace('https://', '');
    setFieldIfDefined('host', host);
  }

  if (!isNil(analyticsFileData.settings.impliedConsent)) {
    const impliedConsent = _toString(analyticsFileData.settings.impliedConsent);
    setFieldIfDefined('impliedConsent', impliedConsent);
  }

  // Skip the stored cookie_name for non-cookie sources — it holds the privacy-block
  // placeholder, and prefilling it would key a later cookie-source switch to a
  // cookie that does not exist on the customer's site.
  const hasNonCookieSource = isNonCookieConsentSource(analyticsFileData.settings.consentSource?.type);
  if (!isNil(analyticsFileData.settings.cookieName) && !hasNonCookieSource) {
    setFieldIfDefined('cookieName', analyticsFileData.settings.cookieName);
  }

  // Legacy rows have no source discriminator; only Override should replace a staged source.
  const consentSource = analyticsFileData.settings.consentSource;
  if (!isNil(consentSource)) {
    setFieldIfDefined(CONSENT_SOURCE_TYPE, consentSource.type);
    setFieldIfDefined(CONSENT_CMP_PRESET, deriveCmpPreset(consentSource));
    setFieldIfDefined(
      CONSENT_SAVED_CALLBACK_SOURCE,
      consentSource.type === CONSENT_SOURCE_TYPES.CALLBACK_JS_API ? JSON.stringify(consentSource) : ''
    );

    if (consentSource.type === CONSENT_SOURCE_TYPES.CMP) {
      setFieldIfDefined(CONSENT_ONETRUST_COOKIE_NAME, consentSource.consentCookieName);
    } else if (consentSource.type === CONSENT_SOURCE_TYPES.JS_API) {
      setFieldIfDefined(CONSENT_JS_API_GLOBAL_OBJECT, consentSource.globalObject);
      setFieldIfDefined(CONSENT_JS_API_METHOD, consentSource.method);
      setFieldIfDefined(CONSENT_JS_API_PROPERTY_PATH, consentSource.propertyPath);
    } else if (consentSource.type === CONSENT_SOURCE_TYPES.COOKIE) {
      setFieldIfDefined('cookieName', consentSource.cookieName);
      setFieldIfDefined(CONSENT_COOKIE_ENCODING, consentSource.encoding);
      setFieldIfDefined(CONSENT_COOKIE_WINDOW_VAR, consentSource.windowVarName);
    }
  }

  if (Array.isArray(analyticsFileData.settings.optInValues)) {
    setFieldIfDefined(CONSENT_OPT_IN_VALUES, formatOptInValues(analyticsFileData.settings.optInValues));
  }

  const globalPrivacyControl = analyticsFileData.settings.globalPrivacyControl;
  if (!isNil(globalPrivacyControl)) {
    setFieldIfDefined(CONSENT_GPC_ENABLED, _toString(globalPrivacyControl.enabled === true));
    setFieldIfDefined(CONSENT_GPC_CODES, JSON.stringify(globalPrivacyControl.codes ?? []));
  }

  if (!isNil(analyticsFileData.settings.itpSettings)) {
    setFieldIfDefined('httpCookie', analyticsFileData.settings.itpSettings === true);
  }

  // copy saved cross-domain settings to formData
  const crossDomainSync = analyticsFileData.identitySyncs.find(s => s.name === 'crossDomain');
  if (crossDomainSync) {
    setField({
      target: {
        name: 'crossDomain',
        value: crossDomainSync.settings.syncURLs.join(', '),
      },
    });
  }

  // copy saved cross-domain-direct settings to formData
  const crossDomainDirectSync = analyticsFileData.identitySyncs.find(s => s.name === 'crossDomainDirect');
  if (crossDomainDirectSync) {
    setField({
      target: {
        name: 'crossDomainDirect',
        value: crossDomainDirectSync.settings.syncURL,
      },
    });
  }

  // copy saved identity sync settings to formData
  analyticsFileData.identitySyncs.forEach(sync => {
    setField({
      target: {
        name: `sync-${sync.name}-consentCategoryId`,
        value: sync.consentCategoryId,
      },
    });

    setField({
      target: {
        name: `sync-${sync.name}-tcfEnabled`,
        value: _toString(sync.tcfEnabled), // 'true' | 'false'
      },
    });

    for (const setting in sync.settings) {
      setField({
        target: {
          name: `sync-${sync.name}-${setting}`,
          value:
            // Stringify objects before submitting to formState
            _isObject(sync.settings[setting]) ? JSON.stringify(sync.settings[setting]) : sync.settings[setting],
        },
      });
    }
  });

  // copy saved google tag settings to formData

  if (analyticsFileData.google_tag_settings) {
    const {
      tagId,
      loadGtag,
      loadWhenConsentDenied,
      refreshOnEmptyHash,
      vtcEnabled,
      gtgEnabled,
      gtgScriptSource,
      egsm,
    } = analyticsFileData.google_tag_settings;

    setField({
      target: {
        name: `sync-parentGTag-parentTagID`,
        value: tagId,
      },
    });

    setField({
      target: {
        name: `sync-parentGTag-loadGtag`,
        value: loadGtag ? 'true' : 'false',
      },
    });

    setField({
      target: {
        name: `sync-parentGTag-loadWhenConsentDenied`,
        value: loadWhenConsentDenied ? 'true' : 'false',
      },
    });

    setField({
      target: {
        name: `sync-parentGTag-refreshOnEmptyHash`,
        value: refreshOnEmptyHash ? 'true' : 'false',
      },
    });

    setField({
      target: {
        name: `sync-parentGTag-vtcEnabled`,
        value: vtcEnabled ? 'true' : 'false',
      },
    });

    setField({
      target: {
        name: `sync-parentGTag-gtgEnabled`,
        value: gtgEnabled ? 'true' : 'false',
      },
    });

    setField({
      target: {
        name: `sync-parentGTag-gtgScriptSource`,
        value: gtgScriptSource ?? '',
      },
    });

    // Written on Override (or when the file carries egsm) so stale EGSM state is
    // cleared; a Merge import without egsm keeps the staged mapping.
    if (updateMode === UPDATE_MODES.OVERRIDE || !isNil(egsm)) {
      setField({
        target: {
          name: `sync-parentGTag-${EGSM_ENABLED_FIELD}`,
          value: egsm?.enabled ? 'true' : 'false',
        },
      });

      EGSM_SIGNALS.forEach(({ signal, formField }) => {
        setField({
          target: {
            name: `sync-parentGTag-${formField}`,
            value: egsm?.[signal] ?? EGSM.NOT_SET,
          },
        });
      });
    }
  }

  // copy saved integration settings to formData
  analyticsFileData.integrations.forEach(integration => {
    for (const option in integration.options) {
      setField({
        target: {
          name: `integration-${integration.name}-${option}`,
          value:
            // Stringify objects before submitting to formState
            _isObject(integration.options[option])
              ? JSON.stringify(integration.options[option])
              : integration.options[option],
        },
      });
    }
  });

  setFieldIfDefined('windowVariableName', analyticsFileData.settings.windowVariableName);
  setFieldIfDefined('storagePrefix', analyticsFileData.settings.storagePrefix);
  setFieldIfDefined('storageType', analyticsFileData.settings.storageType);
  setFieldIfDefined('storageDatabase', analyticsFileData.settings.storageDatabase);
};

export default populateAjsBuilderForm;
